/**
 * Plugin Local Store — `executionMode: 'client-side'` entity executor (ADR-031).
 *
 * On-device storage for a plugin's working data (recovery journals, drafts,
 * caches). The plugin's sandbox has no storage of its own — its iframe has an
 * opaque origin — so the HOST page keeps it: one IndexedDB database on the
 * platform origin, every entry keyed by `[archeId, scope, userId, key]`. The
 * arche is host-derived, the scope is the workspace the user is acting in
 * (the host's holder switcher: an org workspace's holder id, or `personal`)
 * and the user is the host's session — all three arrive in the invocation
 * context, never in the input — so a plugin reaches only its own namespace for
 * the signed-in user in the current workspace. Data a plugin kept while the
 * user worked in one workspace is not readable from another, the same
 * boundary the Vault draws. The host clears a user's namespaces when the
 * session ends (`drainAndClearPluginLocalStoreForUser`) and sweeps every
 * other user's when one signs in (`clearPluginLocalStoreExceptUser`).
 *
 * THREE OBJECT STORES. `entries` holds the values; `meta` holds only
 * `{ sizeBytes, chargedBytes, updatedAt }` per entry; `namespaces` holds one
 * running `{ usedBytes, entryCount }` record per namespace, updated in the
 * same transaction as every write. A write reads two small records whatever
 * the namespace holds, `list` reads `meta`, and nothing but `get` /
 * `get_many` ever deserializes a value.
 *
 * Values are a string, bytes (`ArrayBuffer` / `Uint8Array`) or plain JSON
 * (null, boolean, finite number, string, array, plain object), stored with
 * their kind so `get` returns what `put` was given. Anything else — another
 * typed array, a `Blob`, a `Date`, a `Map` — is refused (`INVALID_INPUT`)
 * rather than silently stored as something it is not. Bytes are stored as
 * `ArrayBuffer`, which every current browser's IndexedDB handles natively at
 * multi-megabyte sizes.
 *
 * Limits: one value ≤ `PLUGIN_LOCAL_STORE_MAX_VALUE_BYTES`; a namespace ≤
 * `PLUGIN_LOCAL_STORE_NAMESPACE_BUDGET_BYTES` (or a quarter of the browser's
 * quota, whichever is smaller) and ≤ `PLUGIN_LOCAL_STORE_MAX_ENTRIES`. An
 * entry is charged its value bytes PLUS its key bytes and a fixed per-entry
 * overhead, so a plugin cannot grow the namespace for free with empty values.
 * A `put` over budget is REFUSED (`LOCAL_STORE_QUOTA_EXCEEDED`); the store
 * never evicts a plugin's data on its own — a recovery journal silently
 * losing its newest entry is worse than a refused write the plugin can react
 * to. The browser's own `QuotaExceededError` is reported under the same code.
 *
 * Everything here runs on the host's main thread, so no input may cost more
 * than its size: a JSON value is measured in one linear walk (shared
 * references are measured once, and a value whose references would EXPAND
 * past the value cap is refused before `JSON.stringify` builds it), and an
 * oversized string or byte value is refused before it is encoded or copied.
 *
 * Failures are RETURNED as `{ error: { code, message } }` rather than thrown,
 * so the code survives the bridge (the same convention as the content pack
 * executor). Invalid input is `INVALID_INPUT` / `LOCAL_STORE_KEY_INVALID`.
 *
 * Host services arrive through `configurePluginLocalStoreHost` at registration
 * time; absent that, the globals are used. Tests inject `fake-indexeddb`.
 */
import type { ClientEntityDefinition, ClientEntityInvocationContext } from './types';

/**
 * Entity ID literal. SYNC NOTE: must match `PLUGIN_LOCAL_STORE_ENTITY_ID` in
 * `packages/entities/src/integrations/plugin-local-store/config.ts` — pinned
 * by `tests/architecture/client-entity-registry-mirrors-entity-config.test.ts`.
 */
export const PLUGIN_LOCAL_STORE_ENTITY_ID = 'ent_intg_plugin_local_store_0000';

/** Largest single value — and the most one `get_many` returns. */
export const PLUGIN_LOCAL_STORE_MAX_VALUE_BYTES = 64 * 1024 * 1024;
/** Largest namespace (one plugin, one workspace, one user), before the quota fraction. */
export const PLUGIN_LOCAL_STORE_NAMESPACE_BUDGET_BYTES = 256 * 1024 * 1024;
/** Fraction of the browser's reported origin quota a namespace may take. */
export const PLUGIN_LOCAL_STORE_QUOTA_FRACTION = 0.25;
/** Most entries in one namespace. */
export const PLUGIN_LOCAL_STORE_MAX_ENTRIES = 10_000;
/** Longest key. */
export const PLUGIN_LOCAL_STORE_MAX_KEY_LENGTH = 256;
/** Most keys one `get_many` reads. */
export const PLUGIN_LOCAL_STORE_MAX_KEYS_PER_READ = 100;
/** Charged per entry on top of its value and key bytes (record + index cost). */
export const PLUGIN_LOCAL_STORE_ENTRY_OVERHEAD_BYTES = 256;
/**
 * Longest an explicit sign-out waits for its clear. Signing out must never be
 * held hostage by device storage (an IndexedDB open that never settles); past
 * this the clear carries on in the background, the store stays closed to
 * plugins, and the next sign-in sweeps whatever it left.
 */
export const PLUGIN_LOCAL_STORE_SIGN_OUT_TIMEOUT_MS = 3_000;

export type PluginLocalStoreErrorCode =
  | 'LOCAL_STORE_UNAVAILABLE'
  | 'LOCAL_STORE_KEY_INVALID'
  | 'LOCAL_STORE_VALUE_TOO_LARGE'
  | 'LOCAL_STORE_QUOTA_EXCEEDED'
  | 'INVALID_INPUT';

export interface PluginLocalStoreError {
  code: PluginLocalStoreErrorCode;
  message: string;
}

/** What a plugin may store. JSON means plain, structured-clone-safe data. */
export type PluginLocalStoreValue = string | ArrayBuffer | Uint8Array | PluginLocalStoreJson;
export type PluginLocalStoreJson =
  | null
  | boolean
  | number
  | string
  | PluginLocalStoreJson[]
  | { [key: string]: PluginLocalStoreJson };

export type PluginLocalStoreInput =
  | { verb: 'get'; key: string }
  | { verb: 'get_many'; keys: string[] }
  | { verb: 'put'; key: string; value: PluginLocalStoreValue }
  | { verb: 'delete'; key: string }
  | { verb: 'list'; prefix?: string }
  | { verb: 'clear' }
  | { verb: 'estimate' };

export interface PluginLocalStoreEntryMeta {
  key: string;
  /** The value's own size; what `put` reported. */
  sizeBytes: number;
  /** ISO-8601. */
  updatedAt: string;
}

export interface PluginLocalStoreEntry extends PluginLocalStoreEntryMeta {
  /** The value as it was put: a string, an `ArrayBuffer` for bytes, or JSON. */
  value: string | ArrayBuffer | PluginLocalStoreJson;
}

export type PluginLocalStoreResult =
  | { entry: PluginLocalStoreEntry | null }
  | { entries: Array<PluginLocalStoreEntry | null> }
  | { sizeBytes: number }
  | { entries: PluginLocalStoreEntryMeta[] }
  | { deleted: number }
  | { usedBytes: number; budgetBytes: number }
  | { error: PluginLocalStoreError }
  | Record<string, never>;

// ---------------------------------------------------------------------------
// Host configuration
// ---------------------------------------------------------------------------

/** The host-driven operations that absorb their failures instead of throwing. */
export type PluginLocalStoreOperation = 'sign-out-clear' | 'sign-in-sweep' | 'role-loss-clear';

export interface PluginLocalStoreHost {
  /** Deployment environment — part of the database name, so environments never share a store. */
  environment: string;
  indexedDB?: IDBFactory;
  storage?: Pick<StorageManager, 'estimate' | 'persist'>;
  /**
   * Where a failure the store absorbs is reported. The sign-out clear and the
   * sign-in sweep never throw — signing in or out must not fail because of
   * device storage — so this is the only trace one leaves.
   */
  reportError?: (operation: PluginLocalStoreOperation, error: unknown) => void;
  /** Test hook: a fixed namespace budget instead of the quota-derived one. */
  budgetBytesOverride?: number;
}

let host: PluginLocalStoreHost | null = null;
let dbPromise: Promise<IDBDatabase> | null = null;
let budgetPromise: Promise<number> | null = null;
let persistRequested = false;
/** Sign-out drain (ADR-031): while set, every executor call is refused so a
 *  plugin cannot write between "the user signed out" and "the clear ran".
 *  Lifted when the next identity arrives (`clearPluginLocalStoreExceptUser`). */
let draining = false;
/** Executor calls currently running, so a drain can wait for them. */
const inFlight = new Set<Promise<unknown>>();
/** The same, per namespace, so a namespace hold drains only its own. */
const inFlightByNamespace = new Map<string, Set<Promise<unknown>>>();
/**
 * Namespaces the host is holding (ADR-034 role loss): a call for one waits
 * for the hold to end before it runs — it is delayed, never refused, so a
 * plugin restoring its journal at boot gets it (or, after a clear, nothing),
 * never a spurious "unavailable".
 */
const holds = new Map<string, Promise<void>>();

export function configurePluginLocalStoreHost(config: PluginLocalStoreHost): void {
  host = config;
  dbPromise = null;
  budgetPromise = null;
  persistRequested = false;
}

export function resetPluginLocalStoreHostForTests(): void {
  host = null;
  dbPromise = null;
  budgetPromise = null;
  persistRequested = false;
  draining = false;
  inFlight.clear();
  inFlightByNamespace.clear();
  holds.clear();
}

const DB_NAME_PREFIX = 'fias-plugin-local-store-';
const DB_VERSION = 3;
/**
 * The last version that changed the record layout (v2 split sizes from values;
 * v3 added the workspace scope to every key). The data is scratch, so a
 * database older than this is dropped on upgrade rather than migrated; a
 * later version that only adds an index must leave it alone.
 */
const LAST_LAYOUT_CHANGE = 3;
const ENTRIES = 'entries';
const META = 'meta';
/** One record per namespace: the running usage and entry count, kept in the
 *  same transaction as every write so a `put` reads two small records
 *  instead of the whole namespace's metadata. */
const NAMESPACES = 'namespaces';
const INDEX_NAMESPACE = 'namespace';
const INDEX_USER = 'user';
/** The scope of the Personal workspace; an org workspace is scoped by its holder id. */
const PERSONAL_SCOPE = 'personal';

type NamespaceKey = [archeId: string, scope: string, userId: string];
type EntryKey = [archeId: string, scope: string, userId: string, key: string];

interface StoredEntry {
  archeId: string;
  scope: string;
  userId: string;
  key: string;
  kind: 'text' | 'bytes' | 'json';
  value: string | ArrayBuffer | PluginLocalStoreJson;
  sizeBytes: number;
  updatedAt: string;
}

interface StoredNamespace {
  archeId: string;
  scope: string;
  userId: string;
  usedBytes: number;
  entryCount: number;
}

interface StoredMeta {
  archeId: string;
  scope: string;
  userId: string;
  key: string;
  /** The value's own size (what the plugin sees). */
  sizeBytes: number;
  /** What the namespace budget is charged: value + key bytes + overhead. */
  chargedBytes: number;
  updatedAt: string;
}

function resolveHost(): PluginLocalStoreHost {
  if (host) return host;
  const g = globalThis as { indexedDB?: IDBFactory; navigator?: { storage?: StorageManager } };
  host = {
    environment: 'default',
    indexedDB: g.indexedDB,
    storage: g.navigator?.storage,
  };
  return host;
}

function databaseName(h: PluginLocalStoreHost): string {
  return `${DB_NAME_PREFIX}${h.environment}`;
}

function report(operation: PluginLocalStoreOperation, err: unknown): void {
  try {
    resolveHost().reportError?.(operation, err);
  } catch {
    // A reporter that throws must not turn an absorbed failure into a thrown one.
  }
}

function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
  });
}

/** Each distinct key of an index, in order — one cursor step per distinct key. */
function distinctIndexKeys(index: IDBIndex): Promise<IDBValidKey[]> {
  return new Promise((resolve, reject) => {
    const keys: IDBValidKey[] = [];
    const request = index.openKeyCursor(null, 'nextunique');
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve(keys);
        return;
      }
      keys.push(cursor.key);
      cursor.continue();
    };
    request.onerror = () => reject(request.error ?? new Error('IndexedDB cursor failed'));
  });
}

/**
 * Whether this origin already has the store's database. The sign-in sweep and
 * the sign-out clear ask first, so a user who never ran a plugin that keeps
 * local data costs nothing — no database is created and nothing is read. A
 * browser that cannot answer (no `databases()`) is treated as "maybe".
 */
async function databaseExists(): Promise<boolean> {
  if (dbPromise) return true;
  const h = resolveHost();
  const factory = h.indexedDB;
  if (!factory) return false;
  if (typeof factory.databases !== 'function') return true;
  try {
    const name = databaseName(h);
    return (await factory.databases()).some((d) => d.name === name);
  } catch {
    return true;
  }
}

function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  const h = resolveHost();
  const factory = h.indexedDB;
  if (!factory) {
    return Promise.reject(unavailable('IndexedDB is not available in this browser'));
  }
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(databaseName(h), DB_VERSION);
    } catch (err) {
      reject(unavailable((err as Error)?.message ?? 'IndexedDB refused to open'));
      return;
    }
    request.onupgradeneeded = (event) => {
      const db = request.result;
      if (event.oldVersion < LAST_LAYOUT_CHANGE) {
        for (const name of Array.from(db.objectStoreNames)) db.deleteObjectStore(name);
      }
      if (!db.objectStoreNames.contains(ENTRIES)) {
        db.createObjectStore(ENTRIES, { keyPath: ['archeId', 'scope', 'userId', 'key'] });
      }
      if (!db.objectStoreNames.contains(META)) {
        const meta = db.createObjectStore(META, {
          keyPath: ['archeId', 'scope', 'userId', 'key'],
        });
        meta.createIndex(INDEX_NAMESPACE, ['archeId', 'scope', 'userId'], { unique: false });
        meta.createIndex(INDEX_USER, 'userId', { unique: false });
      }
      if (!db.objectStoreNames.contains(NAMESPACES)) {
        const ns = db.createObjectStore(NAMESPACES, { keyPath: ['archeId', 'scope', 'userId'] });
        ns.createIndex(INDEX_USER, 'userId', { unique: false });
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      // If another tab upgrades the schema later, drop our handle so the next
      // call reopens instead of failing forever on a closed connection.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    request.onerror = () =>
      reject(unavailable(request.error?.message ?? 'IndexedDB refused to open'));
    request.onblocked = () => reject(unavailable('IndexedDB open was blocked by another tab'));
  }).catch((err) => {
    dbPromise = null;
    throw err;
  });
  return dbPromise;
}

/**
 * Best effort, once, and only after a plugin has actually kept something:
 * ask the browser not to evict this origin under storage pressure. Browsers
 * grant it to engaged first-party sites (and some may ask the user), so it is
 * never asked on behalf of a user who has nothing stored; a refusal changes
 * nothing about how the store works.
 */
function requestPersistenceOnce(): void {
  if (persistRequested) return;
  persistRequested = true;
  void resolveHost()
    .storage?.persist?.()
    .catch(() => undefined);
}

class LocalStoreFailure extends Error {
  constructor(
    public readonly code: PluginLocalStoreErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'LocalStoreFailure';
  }
}

function unavailable(message: string): LocalStoreFailure {
  return new LocalStoreFailure('LOCAL_STORE_UNAVAILABLE', message);
}

function tooLarge(message: string): LocalStoreFailure {
  return new LocalStoreFailure('LOCAL_STORE_VALUE_TOO_LARGE', message);
}

function failure(err: unknown): { error: PluginLocalStoreError } {
  if (err instanceof LocalStoreFailure) return { error: { code: err.code, message: err.message } };
  const name = (err as { name?: unknown })?.name;
  const message = err instanceof Error ? err.message : String(err);
  // The browser's own quota refusal is the one case the plugin is told to act
  // on ("decide what to keep"), so it keeps the quota code — it can happen
  // below the namespace budget when the origin's quota is small or shared.
  if (name === 'QuotaExceededError') {
    return { error: { code: 'LOCAL_STORE_QUOTA_EXCEEDED', message } };
  }
  // Anything else IndexedDB threw (InvalidStateError on a closing connection,
  // a private-mode refusal) reads as the store being unavailable for this
  // call; the plugin degrades to "nothing persisted".
  return { error: { code: 'LOCAL_STORE_UNAVAILABLE', message } };
}

// ---------------------------------------------------------------------------
// Validation + encoding
// ---------------------------------------------------------------------------

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

function validateKey(key: unknown): string {
  if (typeof key !== 'string' || key.length === 0) {
    throw new LocalStoreFailure('LOCAL_STORE_KEY_INVALID', 'key must be a non-empty string');
  }
  if (key.length > PLUGIN_LOCAL_STORE_MAX_KEY_LENGTH) {
    throw new LocalStoreFailure(
      'LOCAL_STORE_KEY_INVALID',
      `key is at most ${PLUGIN_LOCAL_STORE_MAX_KEY_LENGTH} characters`,
    );
  }
  if (CONTROL_CHARS.test(key)) {
    throw new LocalStoreFailure(
      'LOCAL_STORE_KEY_INVALID',
      'key must not contain control characters',
    );
  }
  return key;
}

function validateKeys(keys: unknown): string[] {
  if (!Array.isArray(keys) || keys.length === 0) {
    throw new LocalStoreFailure('INVALID_INPUT', 'keys must be a non-empty array of keys');
  }
  if (keys.length > PLUGIN_LOCAL_STORE_MAX_KEYS_PER_READ) {
    throw new LocalStoreFailure(
      'INVALID_INPUT',
      `one get_many reads at most ${PLUGIN_LOCAL_STORE_MAX_KEYS_PER_READ} keys`,
    );
  }
  return keys.map(validateKey);
}

function invalidValue(detail: string): LocalStoreFailure {
  return new LocalStoreFailure(
    'INVALID_INPUT',
    `value must be a string, bytes (ArrayBuffer or Uint8Array), or plain JSON — ${detail}`,
  );
}

function valueTooLarge(sizeBytes: number): LocalStoreFailure {
  return tooLarge(
    `value is ${sizeBytes} bytes; a value is at most ${PLUGIN_LOCAL_STORE_MAX_VALUE_BYTES} bytes`,
  );
}

function serializesTooLarge(path: string): LocalStoreFailure {
  return tooLarge(
    `${path} serializes to more than ${PLUGIN_LOCAL_STORE_MAX_VALUE_BYTES} bytes, the most a value may be`,
  );
}

/** Deeper than any sane document; a cap so a pathological value cannot
 *  overflow the stack during the walk. */
const MAX_JSON_DEPTH = 256;

/**
 * Refuse anything JSON would silently mangle — non-finite numbers, bigint,
 * functions, symbols, `undefined` array slots, class instances (Date, Map,
 * Set, Blob, other typed arrays), cycles (structured clone preserves them;
 * JSON cannot) and absurd depth — and return a LOWER BOUND on the value's
 * JSON text length.
 *
 * Linear in what the plugin sent: an object reached again through another
 * path is not walked again (its measurement is memoised), and the walk
 * stops as soon as a subtree's text would pass the value cap. That matters
 * because postMessage preserves shared references — a chain of 41 objects,
 * each pointing at the next twice, serializes to 2^40 copies. Walking every
 * path, or handing it to `JSON.stringify`, would freeze the host page; here
 * it is refused as too large after ~26 levels.
 */
function measurePlainJson(
  value: unknown,
  path: string,
  ancestors: WeakSet<object>,
  measured: WeakMap<object, number>,
  depth: number,
): number {
  if (value === null) return 4;
  switch (typeof value) {
    case 'string':
      return value.length + 2;
    case 'boolean':
      return value ? 4 : 5;
    case 'number':
      if (!Number.isFinite(value)) throw invalidValue(`${path} is not a finite number`);
      return 1;
    case 'object': {
      const known = measured.get(value);
      if (known !== undefined) return known;
      if (ancestors.has(value)) throw invalidValue(`${path} is cyclic`);
      if (depth >= MAX_JSON_DEPTH) {
        throw invalidValue(`${path} is nested deeper than ${MAX_JSON_DEPTH}`);
      }
      ancestors.add(value);
      // Brackets, plus one separator between members; each member adds its own text.
      let size = 2;
      let members = 0;
      if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          size += measurePlainJson(value[i], `${path}[${i}]`, ancestors, measured, depth + 1);
          members += 1;
          if (size > PLUGIN_LOCAL_STORE_MAX_VALUE_BYTES) throw serializesTooLarge(path);
        }
      } else {
        const proto = Object.getPrototypeOf(value);
        if (proto !== Object.prototype && proto !== null) {
          throw invalidValue(
            `${path} is a ${(value as object).constructor?.name ?? 'non-plain object'}`,
          );
        }
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
          if (v === undefined) continue; // JSON drops it
          // `"key":` is the key's length plus three characters.
          size +=
            k.length + 3 + measurePlainJson(v, `${path}.${k}`, ancestors, measured, depth + 1);
          members += 1;
          if (size > PLUGIN_LOCAL_STORE_MAX_VALUE_BYTES) throw serializesTooLarge(path);
        }
      }
      size += Math.max(0, members - 1);
      ancestors.delete(value);
      measured.set(value, size);
      return size;
    }
    default:
      throw invalidValue(`${path} is a ${typeof value}`);
  }
}

function isDetached(buffer: ArrayBuffer): boolean {
  return (buffer as { detached?: boolean }).detached === true;
}

/** Normalize a value for storage: its kind, the stored form, and its size. */
function encodeValue(value: unknown): Pick<StoredEntry, 'kind' | 'value' | 'sizeBytes'> {
  if (typeof value === 'string') {
    // A UTF-8 encoding is never shorter than the string's UTF-16 length, so an
    // oversized string is refused before it is encoded.
    if (value.length > PLUGIN_LOCAL_STORE_MAX_VALUE_BYTES) throw valueTooLarge(value.length);
    return { kind: 'text', value, sizeBytes: new TextEncoder().encode(value).byteLength };
  }
  if (value instanceof ArrayBuffer || value instanceof Uint8Array) {
    const buffer = value instanceof Uint8Array ? value.buffer : value;
    if (buffer instanceof ArrayBuffer && isDetached(buffer)) {
      throw invalidValue('the buffer is detached');
    }
    // Size is checked BEFORE the copy, so an oversized input costs no memory.
    if (value.byteLength > PLUGIN_LOCAL_STORE_MAX_VALUE_BYTES)
      throw valueTooLarge(value.byteLength);
    // Copy into an ArrayBuffer sized exactly to the data: a Uint8Array may be
    // a view over a larger buffer, and a buffer that arrived over postMessage
    // is ours to keep.
    const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
    const copy = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(copy).set(bytes);
    return { kind: 'bytes', value: copy, sizeBytes: copy.byteLength };
  }
  if (ArrayBuffer.isView(value)) {
    throw invalidValue(`bytes must be a Uint8Array or ArrayBuffer, not ${value.constructor.name}`);
  }
  if (value === undefined) throw invalidValue('value is undefined');
  measurePlainJson(value, 'value', new WeakSet(), new WeakMap(), 0);
  let text: string;
  try {
    text = JSON.stringify(value);
  } catch (err) {
    throw invalidValue((err as Error)?.message ?? 'it cannot be serialized');
  }
  // Stored as the parsed JSON (structured clone, `undefined` members dropped),
  // sized as its UTF-8 text.
  return {
    kind: 'json',
    value: JSON.parse(text) as PluginLocalStoreJson,
    sizeBytes: new TextEncoder().encode(text).byteLength,
  };
}

function chargedBytes(key: string, sizeBytes: number): number {
  return (
    sizeBytes + new TextEncoder().encode(key).byteLength + PLUGIN_LOCAL_STORE_ENTRY_OVERHEAD_BYTES
  );
}

function toEntry(stored: StoredEntry): PluginLocalStoreEntry {
  return {
    key: stored.key,
    value: stored.value,
    sizeBytes: stored.sizeBytes,
    updatedAt: stored.updatedAt,
  };
}

function toMeta(stored: StoredMeta): PluginLocalStoreEntryMeta {
  return { key: stored.key, sizeBytes: stored.sizeBytes, updatedAt: stored.updatedAt };
}

// ---------------------------------------------------------------------------
// Namespace helpers (inside a caller-owned transaction)
// ---------------------------------------------------------------------------

function scopeOf(context: ClientEntityInvocationContext): string {
  return typeof context.holderId === 'string' && context.holderId
    ? context.holderId
    : PERSONAL_SCOPE;
}

function namespaceKey(context: ClientEntityInvocationContext): NamespaceKey {
  return [context.archeId, scopeOf(context), context.userId];
}

function entryKey(context: ClientEntityInvocationContext, key: string): EntryKey {
  return [context.archeId, scopeOf(context), context.userId, key];
}

/** Every meta record of the namespace — small records, never values. */
async function namespaceMeta(
  meta: IDBObjectStore,
  context: ClientEntityInvocationContext,
): Promise<StoredMeta[]> {
  // A bare compound key is an exact-match range on the index.
  return (await idbRequest(
    meta.index(INDEX_NAMESPACE).getAll(namespaceKey(context)),
  )) as StoredMeta[];
}

async function readNamespace(
  namespaces: IDBObjectStore,
  key: NamespaceKey,
): Promise<StoredNamespace> {
  const stored = (await idbRequest(namespaces.get(key))) as StoredNamespace | undefined;
  return stored ?? { archeId: key[0], scope: key[1], userId: key[2], usedBytes: 0, entryCount: 0 };
}

/** Apply a delta to the namespace record; a record that reaches zero is removed. */
function writeNamespace(
  namespaces: IDBObjectStore,
  record: StoredNamespace,
  bytesDelta: number,
  countDelta: number,
): void {
  const next: StoredNamespace = {
    ...record,
    usedBytes: Math.max(0, record.usedBytes + bytesDelta),
    entryCount: Math.max(0, record.entryCount + countDelta),
  };
  if (next.entryCount === 0) namespaces.delete([record.archeId, record.scope, record.userId]);
  else namespaces.put(next);
}

function namespaceBudgetBytes(): Promise<number> {
  const h = resolveHost();
  if (typeof h.budgetBytesOverride === 'number') return Promise.resolve(h.budgetBytesOverride);
  budgetPromise ??= (async () => {
    const estimate = await h.storage?.estimate?.().catch(() => undefined);
    const quota = estimate?.quota;
    return quota && quota > 0
      ? Math.min(
          PLUGIN_LOCAL_STORE_NAMESPACE_BUDGET_BYTES,
          Math.floor(quota * PLUGIN_LOCAL_STORE_QUOTA_FRACTION),
        )
      : PLUGIN_LOCAL_STORE_NAMESPACE_BUDGET_BYTES;
  })();
  return budgetPromise;
}

const ALL_STORES: string[] = [ENTRIES, META, NAMESPACES];

// ---------------------------------------------------------------------------
// Verbs
// ---------------------------------------------------------------------------

async function get(
  context: ClientEntityInvocationContext,
  key: string,
): Promise<{ entry: PluginLocalStoreEntry | null }> {
  const db = await openDatabase();
  const tx = db.transaction(ENTRIES, 'readonly');
  const stored = (await idbRequest(tx.objectStore(ENTRIES).get(entryKey(context, key)))) as
    | StoredEntry
    | undefined;
  return { entry: stored ? toEntry(stored) : null };
}

/**
 * Several entries in one call — what a plugin restoring a journal needs,
 * without spending a bridge call (and a slot of the bridge's burst window)
 * per key. The sizes are read from `meta` first, so a read that would return
 * more than one value's worth of bytes is refused before any value is loaded.
 */
async function getMany(
  context: ClientEntityInvocationContext,
  keys: string[],
): Promise<{ entries: Array<PluginLocalStoreEntry | null> }> {
  const db = await openDatabase();
  const tx = db.transaction([ENTRIES, META], 'readonly');
  const meta = tx.objectStore(META);
  const entries = tx.objectStore(ENTRIES);
  const metas = await Promise.all(
    keys.map((k) => idbRequest(meta.get(entryKey(context, k))) as Promise<StoredMeta | undefined>),
  );
  const totalBytes = metas.reduce((sum, m) => sum + (m?.sizeBytes ?? 0), 0);
  if (totalBytes > PLUGIN_LOCAL_STORE_MAX_VALUE_BYTES) {
    throw tooLarge(
      `these ${keys.length} entries hold ${totalBytes} bytes; one get_many returns at most ` +
        `${PLUGIN_LOCAL_STORE_MAX_VALUE_BYTES} bytes — read fewer keys at a time`,
    );
  }
  const stored = await Promise.all(
    keys.map((k, i) =>
      metas[i]
        ? (idbRequest(entries.get(entryKey(context, k))) as Promise<StoredEntry | undefined>)
        : Promise.resolve(undefined),
    ),
  );
  return { entries: stored.map((s) => (s ? toEntry(s) : null)) };
}

async function put(
  context: ClientEntityInvocationContext,
  key: string,
  rawValue: unknown,
): Promise<{ sizeBytes: number }> {
  const encoded = encodeValue(rawValue);
  if (encoded.sizeBytes > PLUGIN_LOCAL_STORE_MAX_VALUE_BYTES) {
    throw valueTooLarge(encoded.sizeBytes);
  }
  const budget = await namespaceBudgetBytes();
  const db = await openDatabase();
  // The namespace record and the overwritten entry's meta are read inside the
  // write transaction, so two tabs cannot both squeeze under the budget on
  // the same stale reading; only IndexedDB requests are awaited while it is
  // open. Two small reads, whatever the namespace holds.
  const tx = db.transaction(ALL_STORES, 'readwrite');
  const meta = tx.objectStore(META);
  const namespaces = tx.objectStore(NAMESPACES);
  const k = entryKey(context, key);
  const [record, previous] = await Promise.all([
    readNamespace(namespaces, namespaceKey(context)),
    idbRequest(meta.get(k)) as Promise<StoredMeta | undefined>,
  ]);
  const charged = chargedBytes(key, encoded.sizeBytes);
  const previousCharged = previous?.chargedBytes ?? 0;
  const countDelta = previous ? 0 : 1;
  if (record.entryCount + countDelta > PLUGIN_LOCAL_STORE_MAX_ENTRIES) {
    tx.abort();
    throw new LocalStoreFailure(
      'LOCAL_STORE_QUOTA_EXCEEDED',
      `this plugin's local store already holds ${PLUGIN_LOCAL_STORE_MAX_ENTRIES} entries`,
    );
  }
  const usedByOthers = record.usedBytes - previousCharged;
  if (usedByOthers + charged > budget) {
    tx.abort();
    throw new LocalStoreFailure(
      'LOCAL_STORE_QUOTA_EXCEEDED',
      `this plugin's local store would exceed its ${budget}-byte budget ` +
        `(${usedByOthers} bytes in use, ${charged} bytes requested)`,
    );
  }
  const updatedAt = new Date().toISOString();
  const [archeId, scope, userId] = namespaceKey(context);
  const entry: StoredEntry = {
    archeId,
    scope,
    userId,
    key,
    kind: encoded.kind,
    value: encoded.value,
    sizeBytes: encoded.sizeBytes,
    updatedAt,
  };
  const metaRecord: StoredMeta = {
    archeId,
    scope,
    userId,
    key,
    sizeBytes: encoded.sizeBytes,
    chargedBytes: charged,
    updatedAt,
  };
  tx.objectStore(ENTRIES).put(entry);
  meta.put(metaRecord);
  writeNamespace(namespaces, record, charged - previousCharged, countDelta);
  await transactionDone(tx);
  requestPersistenceOnce();
  return { sizeBytes: encoded.sizeBytes };
}

async function remove(context: ClientEntityInvocationContext, key: string): Promise<void> {
  const db = await openDatabase();
  const tx = db.transaction(ALL_STORES, 'readwrite');
  const meta = tx.objectStore(META);
  const namespaces = tx.objectStore(NAMESPACES);
  const k = entryKey(context, key);
  const [record, previous] = await Promise.all([
    readNamespace(namespaces, namespaceKey(context)),
    idbRequest(meta.get(k)) as Promise<StoredMeta | undefined>,
  ]);
  if (previous) {
    tx.objectStore(ENTRIES).delete(k);
    meta.delete(k);
    writeNamespace(namespaces, record, -previous.chargedBytes, -1);
  }
  await transactionDone(tx);
}

async function list(
  context: ClientEntityInvocationContext,
  prefix: string | undefined,
): Promise<{ entries: PluginLocalStoreEntryMeta[] }> {
  const db = await openDatabase();
  const tx = db.transaction(META, 'readonly');
  const all = await namespaceMeta(tx.objectStore(META), context);
  const entries = all
    .filter((m) => (prefix ? m.key.startsWith(prefix) : true))
    .map(toMeta)
    .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return { entries };
}

async function clear(context: ClientEntityInvocationContext): Promise<{ deleted: number }> {
  const db = await openDatabase();
  const tx = db.transaction(ALL_STORES, 'readwrite');
  const meta = tx.objectStore(META);
  const entries = tx.objectStore(ENTRIES);
  const all = await namespaceMeta(meta, context);
  for (const m of all) {
    const k: EntryKey = [m.archeId, m.scope, m.userId, m.key];
    entries.delete(k);
    meta.delete(k);
  }
  tx.objectStore(NAMESPACES).delete(namespaceKey(context));
  await transactionDone(tx);
  return { deleted: all.length };
}

async function estimate(
  context: ClientEntityInvocationContext,
): Promise<{ usedBytes: number; budgetBytes: number }> {
  const budgetBytes = await namespaceBudgetBytes();
  const db = await openDatabase();
  const tx = db.transaction(NAMESPACES, 'readonly');
  const record = await readNamespace(tx.objectStore(NAMESPACES), namespaceKey(context));
  return { usedBytes: record.usedBytes, budgetBytes };
}

// ---------------------------------------------------------------------------
// Identity: sign-out clear and sign-in sweep (host-driven, never throw)
// ---------------------------------------------------------------------------

/** Delete every entry and namespace record of these users, in every arche
 *  and workspace, in one transaction. Reads keys through the user indexes,
 *  never values. */
async function deleteUsers(db: IDBDatabase, userIds: readonly string[]): Promise<number> {
  if (userIds.length === 0) return 0;
  const tx = db.transaction(ALL_STORES, 'readwrite');
  const meta = tx.objectStore(META);
  const entries = tx.objectStore(ENTRIES);
  const namespaces = tx.objectStore(NAMESPACES);
  const perUser = await Promise.all(
    userIds.map((userId) =>
      Promise.all([
        idbRequest(meta.index(INDEX_USER).getAllKeys(userId)) as Promise<EntryKey[]>,
        idbRequest(namespaces.index(INDEX_USER).getAllKeys(userId)) as Promise<NamespaceKey[]>,
      ]),
    ),
  );
  let deleted = 0;
  for (const [entryKeys, namespaceKeys] of perUser) {
    for (const k of entryKeys) {
      entries.delete(k);
      meta.delete(k);
      deleted += 1;
    }
    for (const k of namespaceKeys) namespaces.delete(k);
  }
  await transactionDone(tx);
  return deleted;
}

/**
 * Delete every entry a user has, across all plugins and workspaces — the
 * explicit sign-out's clear (through `drainAndClearPluginLocalStoreForUser`),
 * so a recovery journal never outlives a session the user chose to end. (An
 * involuntary drop — an expired cookie, a failed refresh — deliberately does
 * NOT clear: the same user signing back in is the moment the journal exists
 * for; a different user signing in sweeps it, see
 * `clearPluginLocalStoreExceptUser`.) Returns how many entries were removed;
 * never throws (a failure goes to the host's `reportError`).
 */
export async function clearPluginLocalStoreForUser(userId: string): Promise<number> {
  try {
    if (!(await databaseExists())) return 0;
    return await deleteUsers(await openDatabase(), [userId]);
  } catch (err) {
    report('sign-out-clear', err);
    return 0;
  }
}

/**
 * Delete every entry that does NOT belong to `userId` — the host calls this
 * whenever an identity arrives (sign-in, or the boot probe finding a live
 * session), so whatever another user left behind (expired without a
 * sign-out, a guest's trial, a tab that was still writing after another tab
 * signed out) is gone before this user's plugins run. Cheap on the common
 * path: nothing at all when the database does not exist, and otherwise one
 * cursor step per distinct user before any write. Lifts the sign-out drain.
 * Never throws (a failure goes to the host's `reportError`).
 */
export async function clearPluginLocalStoreExceptUser(userId: string): Promise<number> {
  draining = false;
  try {
    if (!(await databaseExists())) return 0;
    const db = await openDatabase();
    const read = db.transaction(META, 'readonly');
    const users = (await distinctIndexKeys(read.objectStore(META).index(INDEX_USER))) as string[];
    return await deleteUsers(
      db,
      users.filter((u) => u !== userId),
    );
  } catch (err) {
    report('sign-in-sweep', err);
    return 0;
  }
}

/** One plugin's data for one user in one workspace — exactly what the executor keys by. */
export interface PluginLocalStoreNamespace {
  archeId: string;
  holderId: string | null;
  userId: string;
}

/** What a held namespace can do: clear itself, its own calls already drained. */
export interface HeldPluginLocalStoreNamespace {
  /** Delete every entry; `ok: false` when the store failed (reported, never thrown). */
  clear(): Promise<{ ok: boolean; deleted: number }>;
}

function namespaceId(n: { archeId: string; holderId?: string | null; userId: string }): string {
  return [n.archeId, scopeOf(n), n.userId].join('\u0000');
}

/**
 * Hold one namespace while `work` decides about it (ADR-034: has this person
 * lost one of the app's roles here?). Calls for that namespace that arrive
 * meanwhile WAIT; calls already running are drained before `work` starts, so
 * a clear inside it cannot be overtaken by a write that began before it. The
 * hold is released when `work` settles, or after `maxHoldMs` — a slow
 * decision must not stall the plugin for long; a clear that `work` still
 * makes after that runs on its own drained transaction. Other namespaces are
 * never held. Never throws for the store's sake; `work`'s own errors
 * propagate.
 */
export async function holdPluginLocalStoreNamespace<T>(
  namespace: PluginLocalStoreNamespace,
  work: (held: HeldPluginLocalStoreNamespace) => Promise<T>,
  options: { maxHoldMs?: number } = {},
): Promise<T> {
  const id = namespaceId(namespace);
  // The calls already running — taken NOW, before the hold exists: a call
  // that arrives later waits on the hold, and draining it would deadlock.
  const running = Array.from(inFlightByNamespace.get(id) ?? []);
  const previous = holds.get(id) ?? Promise.resolve();
  let release!: () => void;
  const released = new Promise<void>((resolve) => {
    release = resolve;
  });
  // Chained: a second hold on the same namespace waits for the first.
  const hold = previous.then(() => released);
  holds.set(id, hold);
  const timer =
    options.maxHoldMs !== undefined ? setTimeout(() => release(), options.maxHoldMs) : undefined;
  try {
    await previous;
    await Promise.allSettled(running);
    return await work({
      clear: async () => {
        try {
          if (!(await databaseExists())) return { ok: true, deleted: 0 };
          return { ok: true, deleted: (await clear(namespace)).deleted };
        } catch (err) {
          report('role-loss-clear', err);
          return { ok: false, deleted: 0 };
        }
      },
    });
  } finally {
    if (timer) clearTimeout(timer);
    release();
    if (holds.get(id) === hold) holds.delete(id);
  }
}

/**
 * Delete one plugin's entries for one user in one workspace — when that user
 * has LOST access the app's data needs (ADR-034), so working data they read
 * while they had it does not outlive it on this device. Exactly the
 * namespace the executor writes, from host-derived ids, under a hold: the
 * namespace's running calls are drained first and new ones wait. Cheap when
 * nothing was ever stored. Never throws (a failure is reported to the host's
 * `reportError` and answered `ok: false`, so a caller can try again).
 */
export async function clearPluginLocalStoreNamespace(
  namespace: PluginLocalStoreNamespace,
): Promise<{ ok: boolean; deleted: number }> {
  return holdPluginLocalStoreNamespace(namespace, (held) => held.clear());
}

/**
 * The sign-out sequence, in order: refuse every new call (a plugin that is
 * still mounted gets `LOCAL_STORE_UNAVAILABLE`, which it already treats as
 * "nothing persisted"), wait for the calls already running to settle, then
 * clear the user. Nothing can be written between the sign-out and the clear.
 * The gate stays closed until the next identity arrives.
 *
 * Bounded by `timeoutMs`: past it this resolves (reporting the overrun) so
 * the sign-out completes, while the clear carries on in the background with
 * the gate still closed; the next sign-in sweeps anything it did not reach.
 * Never throws.
 */
export async function drainAndClearPluginLocalStoreForUser(
  userId: string,
  timeoutMs: number = PLUGIN_LOCAL_STORE_SIGN_OUT_TIMEOUT_MS,
): Promise<number> {
  draining = true;
  const work = (async () => {
    await Promise.allSettled(Array.from(inFlight));
    return clearPluginLocalStoreForUser(userId);
  })();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const overrun = new Promise<number>((resolve) => {
    timer = setTimeout(() => {
      report(
        'sign-out-clear',
        new Error(
          `the sign-out clear did not finish within ${timeoutMs} ms; it continues in the ` +
            'background and the next sign-in sweeps whatever it leaves',
        ),
      );
      resolve(0);
    }, timeoutMs);
  });
  try {
    return await Promise.race([work, overrun]);
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Executor + definition
// ---------------------------------------------------------------------------

export function pluginLocalStoreExecutor(
  input: PluginLocalStoreInput,
  context: ClientEntityInvocationContext,
): Promise<PluginLocalStoreResult> {
  if (draining) {
    return Promise.resolve(
      failure(unavailable('the store is closing: the user is signing out of this device')),
    );
  }
  const id = namespaceId(context);
  const hold = holds.get(id);
  // A call that waited on a hold re-checks the sign-out gate when it runs:
  // a sign-out that began meanwhile refuses it like any new call.
  const run = hold
    ? hold.then(() =>
        draining
          ? failure(unavailable('the store is closing: the user is signing out of this device'))
          : execute(input, context),
      )
    : execute(input, context);
  inFlight.add(run);
  let mine = inFlightByNamespace.get(id);
  if (!mine) inFlightByNamespace.set(id, (mine = new Set()));
  mine.add(run);
  void run.finally(() => {
    inFlight.delete(run);
    mine.delete(run);
    if (mine.size === 0 && inFlightByNamespace.get(id) === mine) inFlightByNamespace.delete(id);
  });
  return run;
}

async function execute(
  input: PluginLocalStoreInput,
  context: ClientEntityInvocationContext,
): Promise<PluginLocalStoreResult> {
  try {
    if (!input || typeof input !== 'object' || typeof input.verb !== 'string') {
      throw new LocalStoreFailure('INVALID_INPUT', 'input.verb is required');
    }
    if (!context?.archeId || !context?.userId) {
      throw new LocalStoreFailure(
        'INVALID_INPUT',
        'invocation context is missing archeId or userId',
      );
    }
    switch (input.verb) {
      case 'get':
        return await get(context, validateKey(input.key));
      case 'get_many':
        return await getMany(context, validateKeys((input as { keys?: unknown }).keys));
      case 'put':
        return await put(context, validateKey(input.key), input.value);
      case 'delete':
        await remove(context, validateKey(input.key));
        return {};
      case 'list': {
        const prefix = (input as { prefix?: unknown }).prefix;
        if (prefix !== undefined) {
          if (typeof prefix !== 'string') {
            throw new LocalStoreFailure('INVALID_INPUT', 'prefix must be a string');
          }
          if (prefix.length > PLUGIN_LOCAL_STORE_MAX_KEY_LENGTH) {
            throw new LocalStoreFailure(
              'INVALID_INPUT',
              `prefix is at most ${PLUGIN_LOCAL_STORE_MAX_KEY_LENGTH} characters`,
            );
          }
        }
        return await list(context, prefix);
      }
      case 'clear':
        return await clear(context);
      case 'estimate':
        return await estimate(context);
      default:
        throw new LocalStoreFailure(
          'INVALID_INPUT',
          `unknown verb "${String((input as { verb: unknown }).verb)}"`,
        );
    }
  } catch (err) {
    return failure(err);
  }
}

export const pluginLocalStoreDefinition = {
  entityId: PLUGIN_LOCAL_STORE_ENTITY_ID,
  rateLimitPerMinute: 300,
  // Storage-family permission (beside `storage:sandbox`), visible by name to
  // the user because it keeps their content on the device — NOT the generic
  // `entities:client_invoke`. The entity config declares
  // `clientSideRole: 'device-storage'`, which is what pins this pairing.
  pluginPermission: 'storage:local',
  executor: pluginLocalStoreExecutor,
} as const satisfies ClientEntityDefinition<PluginLocalStoreInput, PluginLocalStoreResult>;
